import { useState, type FormEvent } from "react";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import Radio from "lucide-react/dist/esm/icons/radio";
import type { Card } from "../../game";
import type { ConnectionStatus } from "../use-game";
import { PlayingCard } from "./playing-card";

interface LobbyProps {
  connection: ConnectionStatus;
  busy: boolean;
  onCreate: (name: string) => Promise<unknown>;
  onJoin: (code: string, name: string) => Promise<unknown>;
  onOpenRules: () => void;
}

const HERO_CARDS: Card[] = [
  { id: "hero-spades-13", rank: 13, suit: "spades" },
  { id: "hero-hearts-1", rank: 1, suit: "hearts" },
  { id: "hero-diamonds-12", rank: 12, suit: "diamonds" },
];

function storedName(): string {
  try {
    return localStorage.getItem("chin:player-name:v1") ?? "";
  } catch {
    return "";
  }
}

function invitedCode(): string {
  return new URLSearchParams(window.location.search).get("sala")?.toUpperCase() ?? "";
}

export function Lobby({ connection, busy, onCreate, onJoin, onOpenRules }: LobbyProps) {
  const initialCode = invitedCode();
  const [mode, setMode] = useState<"create" | "join">(initialCode ? "join" : "create");
  const [name, setName] = useState(storedName);
  const [code, setCode] = useState(initialCode);
  const offline = connection !== "online";

  function updateName(value: string): void {
    const next = value.slice(0, 18);
    setName(next);
    try {
      localStorage.setItem("chin:player-name:v1", next);
    } catch {
      // Storage is an enhancement, never a blocker.
    }
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (name.trim().length < 2 || busy || offline) return;
    if (mode === "create") void onCreate(name.trim());
    else if (code.trim().length >= 4) void onJoin(code.trim(), name.trim());
  }

  return (
    <main className="lobby-shell">
      <header className="lobby-nav">
        <a className="wordmark" href="/" aria-label="CHIN, inicio">CHIN!</a>
        <button className="icon-text-button" type="button" onClick={onOpenRules}>
          <BookOpen aria-hidden="true" size={19} strokeWidth={2.2} />
          <span>Cómo jugar</span>
        </button>
      </header>

      <section className="lobby-hero" aria-labelledby="hero-title">
        <div className="lobby-copy">
          <p className="eyebrow"><Radio aria-hidden="true" size={15} /> 1 vs 1 · en tiempo real</p>
          <h1 id="hero-title">Pensá rápido.<br /><em>Jugá más rápido.</em></h1>
          <p className="lobby-lead">Cartas consecutivas, reflejos y un solo grito que cambia la mesa.</p>
        </div>

        <div className="hero-cards" aria-hidden="true">
          {HERO_CARDS.map((card, index) => (
            <div className={`hero-card hero-card--${index + 1}`} key={card.id}>
              <PlayingCard card={card} size="center" />
            </div>
          ))}
          <span className="hero-burst">¡CHIN!</span>
        </div>

        <form className="lobby-panel" onSubmit={submit}>
          <div className="mode-switch" aria-label="Elegir cómo entrar">
            <button
              type="button"
              className={mode === "create" ? "is-active" : ""}
              aria-pressed={mode === "create"}
              onClick={() => setMode("create")}
            >
              Crear mesa
            </button>
            <button
              type="button"
              className={mode === "join" ? "is-active" : ""}
              aria-pressed={mode === "join"}
              onClick={() => setMode("join")}
            >
              Tengo código
            </button>
          </div>

          <label className="field-label" htmlFor="player-name">Tu nombre</label>
          <input
            id="player-name"
            className="text-field"
            value={name}
            onChange={(event) => updateName(event.target.value)}
            placeholder="Ej. Mica"
            autoComplete="nickname"
            enterKeyHint={mode === "create" ? "go" : "next"}
            maxLength={18}
            required
          />

          {mode === "join" ? (
            <>
              <label className="field-label" htmlFor="room-code">Código de mesa</label>
              <input
                id="room-code"
                className="text-field text-field--code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase().slice(0, 6))}
                placeholder="ABC123"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                maxLength={6}
                required
              />
            </>
          ) : null}

          <button
            className="primary-button"
            type="submit"
            disabled={busy || offline || name.trim().length < 2 || (mode === "join" && code.length < 4)}
          >
            {busy ? "Preparando…" : offline ? "Conectando…" : mode === "create" ? "Crear partida" : "Entrar a jugar"}
          </button>

          <p className="form-hint">Sin registro · baraja de póker · ideal con sonido</p>
        </form>
      </section>

      <footer className="lobby-footer">
        <span className={`connection-dot connection-dot--${connection}`} />
        {connection === "online" ? "Servidor listo" : "Buscando servidor"}
        <span aria-hidden="true">·</span>
        <span>Hecho para tocar, no esperar</span>
      </footer>
    </main>
  );
}
